<template>
  <div class="ps-component-wrapper" v-if="hasBuiltCart">

    <transition name="fade">
      <div v-show="mini && $store.miniCartActive" class="site-mini-cart">
        <button @click.prevent="$actions.closeAll" class="mini-cart__close btn btn--close">
          <IconClose />
        </button>
        <transition name="fade" mode="out-in">
          <form v-if="cart.item_count" action="/cart" method="post" novalidate>
            <ul class="mini-cart__ul">
              <transition-group name="fade" mode="out-in">
                <li v-for="item in cart.items" :key="item.product_id" class="mini-cart__li">
                  <a :href="item.url" class="mini-cart__image">
                    <img :src="item.image">
                  </a>
                  <div class="mini-cart__details">
                    <span class="mini-cart__details-title">
                      {{ item.product_title }}
                    </span>
                    <span class="mini-cart__details-price">
                      {{ item.quantity }} x 
                      <span
                        v-if="hasAutoDiscount(item)" 
                        class="mini-cart__original-price"> 
                        {{ money(item.original_price) }} 
                      </span> 
                      <span
                        v-else-if="hasComparePrice(item)"
                        class="mini-cart__original-price">
                        {{ money(item.properties._compare_price) }} 
                      </span> 
                      {{ money(item.line_price) }}
                    </span>
                    <button class="mini-cart__remove btn btn--close" @click.prevent="updateQuantity(item.id, 0)">
                      <IconClose />
                    </button>
                  </div>
                </li>
              </transition-group>
            </ul>
            <div v-if="cart.total_discount > 0" class="mini-cart__discount-applied">Discount applied</div>
            <span class="mini-cart__total-items">{{ cart.item_count }} items in your cart</span>
            <a href="/cart" class="btn btn--primary btn--full">{{ $language.cart.view }}</a>
          </form>
          <div v-else class="mini-cart__empty">
            <p>{{ $language.cart.empty }}</p>
          </div>
        </transition>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <form v-if="!mini && cart.item_count" class="site-cart" action="/cart" method="post" novalidate>
        <div class="cart__header mta-center">
          <div class="cart__header-left">
            <h1 class="cart__title">{{ $language.cart.title }}</h1>
            <span class="cart__total-items">({{ cart.item_count }} items)</span>
          </div>
          <div class="cart__header-right tablet-up">
            <span class="cart__total-price dta-right" :class="{ 'disabled--text': isUpdating }">{{ $language.cart.subtotal }} {{ money(cart.total_price) }}</span>
            <input type="submit" name="checkout" class="btn btn--primary cart__checkout" value="Checkout" :disabled="isUpdating">
          </div>
        </div>
        <ul class="cart__ul">
          <transition name="fade">
            <li v-if="free_shipping" class="cart__free-shipping">
              {{ $language.cart.free_shipping }}
            </li>
          </transition>
          <transition-group name="fade" mode="out-in">
            <li v-for="item in cart.items" :key="item.product_id" class="cart__li">
              <a :href="item.url" class="cart__image">
                <img :src="item.image">
              </a>
              <div class="cart__details">
                <span class="cart__details-title">
                  {{ item.product_title }}
                </span>
                <span v-if="!item.product_has_only_default_variant" class="cart__details-variant">
                  {{ item.variant_title }}
                </span>
                <span class="cart__details-price" :class="{ 'disabled--text': isUpdating }">
                  <span
                    v-if="hasAutoDiscount(item)"
                    class="mini-cart__original-price">
                    {{ money(item.original_price) }} 
                  </span> 
                  <span
                    v-else-if="hasComparePrice(item)"
                    class="mini-cart__original-price">
                    {{ calcComparePrice(item.properties._compare_price, item.quantity) }} 
                  </span>
                  {{ money(item.line_price) }}
                </span>
                <div class="item__quantity">
                  <button class="item__quantity-minus" @click="updateQuantity(item.id, (item.quantity - 1))" :disabled="isUpdating">-</button>
                  <input class="item__quantity-input" name="updates[]" type="text" :value="item.quantity" pattern="[0-9]*" min="0" aria-label="quantity" :disabled="isUpdating">
                  <button class="item__quantity-plus" @click="updateQuantity(item.id, (item.quantity + 1))" :disabled="isUpdating">+</button>
                </div>
                <button class="cart__remove btn btn--close" @click.prevent="updateQuantity(item.id, 0)">{{ $language.cart.remove }}</button>
              </div>
            </li>
          </transition-group>
        </ul>
        <div class="cart__actions">
          <div class="cart__note">
            <label class="cart__note-btn" for="cart-note">{{ cart.note ? 'Edit note' : 'Add note' }}</label>
            <textarea v-model="cart.note" @keyup="updateNote()" class="cart__note-textarea" name="note" id="cart-note" rows="3" :placeholder="$language.cart.note"></textarea>
          </div>
          <input type="submit" name="checkout" class="btn btn--primary cart__checkout" :value="$language.cart.checkout" :disabled="isUpdating">
          <span class="cart__total-price mobile-down mta-center" :class="{ 'disabled--text': isUpdating }">{{ $language.cart.subtotal }} {{ money(cart.total_price) }}</span>
        </div>
      </form>
      <div v-else-if="!mini" class="cart__empty">
        <p>{{ $language.cart.empty }}</p><br>
        <a href="/collections/all">Continue Shopping</a>
      </div>
    </transition>

  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { formatMoney } from '@shopify/theme-currency'
import IconClose from '../Icons/IconClose'

export default {
  components: { IconClose },

  props: [ 'mini', 'show_free_shipping', 'free_shipping_threshold' ],

  computed: {
    free_shipping () {
      return this.show_free_shipping == 'true' && this.number(this.cart.total_price) > parseInt(this.free_shipping_threshold)
    }
  },

  data () {
    return {
      cart           : {},
      currency_format: '${{amount}}',
      number_format  : '{{amount}}',
      isUpdating     : true,
      hasBuiltCart   : false
    }
  },

  methods: {
    buildCart () {
      fetch('/cart.js')
        .then(response => response.json())
        .then(data => {
          this.cart = { ...data }
          this.isUpdating = false
          this.hasBuiltCart = true
        })
    },

    updateQuantity (id, qty) {
      this.isUpdating = true
      fetch('/cart/change.js', {
        method: 'post',
        body: new URLSearchParams(`quantity=${ qty }&id=${ id }`)
      })
      .then(response => response.json())
      .then(data => {
        this.buildCart()
      })
    },

    updateNote: debounce(function () {
      fetch('/cart/update.js', {
        method: 'post',
        body: new URLSearchParams(`note=${ this.cart.note }`),
      })
    }, 1000),

    money (amount) {
      return formatMoney(amount, this.currency_format)
    },

    number (amount) {
      return formatMoney(amount, this.number_format)
    },

    popInOut () {
      this.$actions.toggleMiniCart()
      setTimeout(() => {
        if (this.$store.miniCartActive) {
          this.$actions.toggleMiniCart()
        }
      }, 1500)
    },

    hasComparePrice (item) {
      return item.properties != null && item.properties._compare_price != null
    },

    calcComparePrice (cPrice, qty) {
      let totalComparePrice = cPrice * qty;
      return formatMoney(totalComparePrice, this.currency_format)
    },

    hasAutoDiscount (item) {
      return item.total_discount > 0 
    }
  },

  watch: {
    cart () {
      // Update the cart count bubble
      document.querySelector('.cart-item-count').innerText = this.cart.item_count
    }
  },

  mounted () {
    this.buildCart()
  }
}
</script>
